import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import BackButton from '../components/BackButton';

function formatCentimeters(valueMm) {
  return (valueMm / 10).toFixed(1).replace('.', ',');
}

function buildFormatOptionLabel(format) {
  return `${format.label} - ${formatCentimeters(format.width_mm)} x ${formatCentimeters(format.height_mm)} cm`;
}

function VariantManagerPage() {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [formats, setFormats] = useState([]);
  const [variants, setVariants] = useState([]);
  const [selectedFormat, setSelectedFormat] = useState('a5');
  const [variantName, setVariantName] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const [bookRes, formatsRes, variantsRes] = await Promise.all([
          axios.get(`/api/book/${bookId}`),
          axios.get('/api/variant-formats'),
          axios.get(`/api/books/${bookId}/variants`),
        ]);
        setBook(bookRes.data);
        setFormats(formatsRes.data);
        setVariants(variantsRes.data);
      } catch (err) {
        setError('Failed to load variant tools.');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [bookId]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    setError('');

    try {
      const response = await axios.post(`/api/books/${bookId}/clone-to-variant`, {
        name: variantName || undefined,
        target_format_key: selectedFormat,
        resize_mode: 'fit',
      });

      navigate(`/variants/${response.data.variant_id}/pages`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create variant.');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteVariant = async (variantId, variantName) => {
    const confirmed = window.confirm(
      `Delete variant "${variantName}"? This will remove its generated pages and images.`,
    );
    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(`/api/variants/${variantId}`);
      setVariants((prev) => prev.filter((variant) => variant._id !== variantId));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete variant.');
    }
  };

  if (loading) {
    return <div className="max-w-5xl mx-auto mt-16">Loading variant tools...</div>;
  }

  return (
    <div className="max-w-5xl mx-auto mt-10 p-6">
      <BackButton className="absolute" />

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Book Variants</h1>
          <p className="text-gray-600 mt-2">
            Create alternate-size versions of <span className="font-semibold">{book?.name}</span> without affecting the original book.
          </p>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700">
            {error}
          </div>
        )}

        <form
          onSubmit={handleCreate}
          className="grid gap-4 md:grid-cols-3 bg-gray-50 rounded-lg p-4 border border-gray-200"
        >
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Variant Name
            </label>
            <input
              type="text"
              value={variantName}
              onChange={(e) => setVariantName(e.target.value)}
              placeholder={`${book?.name || 'Book'} - A5`}
              className="w-full rounded-lg border border-gray-300 px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Target Format
            </label>
            <select
              value={selectedFormat}
              onChange={(e) => setSelectedFormat(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 bg-white"
            >
              {formats.map((format) => (
                <option key={format.key} value={format.key}>
                  {buildFormatOptionLabel(format)}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-3 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              The clone uses the original page images and resizes them with `fit`.
            </p>
            <button
              type="submit"
              disabled={creating}
              className="rounded-lg bg-gray-900 px-4 py-2 text-white hover:bg-gray-800 disabled:opacity-60"
            >
              {creating ? 'Creating Variant...' : 'Clone To New Format'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">Existing Variants</h2>
          <span className="text-sm text-gray-500">{variants.length} total</span>
        </div>

        {variants.length === 0 ? (
          <p className="text-gray-500">No variants created yet.</p>
        ) : (
          <div className="space-y-3">
            {variants.map((variant) => (
              <div
                key={variant._id}
                className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3"
              >
                <div>
                  <div className="font-medium text-gray-900">{variant.name}</div>
                  <div className="text-sm text-gray-500">
                    {variant.target_format_label} • {variant.resize_mode}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Link
                    to={`/variants/${variant._id}/pages`}
                    className="rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Open
                  </Link>
                  <button
                    type="button"
                    onClick={() => handleDeleteVariant(variant._id, variant.name)}
                    className="rounded-lg border border-red-300 px-3 py-2 text-sm text-red-700 hover:bg-red-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default VariantManagerPage;
